import React from "react";
import { useLocation } from "react-router-dom";
import { customerVerifyPayment, customerVerifyGripPayment } from "../../api";
import usePaymentVerification, { PAYMENT_MESSAGES } from "../../hooks/usePaymentVerification";
import styles from "./WhatsappSuccess.module.css";

const WhatsappSuccess = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const cartId = searchParams.get("cart_id");
  const type = searchParams.get("type") || "string";
  const paymentOption = searchParams.get("payment_option");
  const freeBooking = searchParams.get("freeBooking");

  // In-store payments and free bookings have nothing to verify.
  const skipVerification = paymentOption === "in-store" || freeBooking === "1";
  const verifyFn = type === "grip" ? customerVerifyGripPayment : customerVerifyPayment;

  const { status: verifyStatus, checkAgain } = usePaymentVerification(
    verifyFn,
    cartId,
    !!cartId && !skipVerification
  );

  // 'verifying' | 'success' | 'processing' | 'failed'
  let status = verifyStatus;
  let message = "Verifying payment status...";
  if (!cartId) {
    status = "failed";
    message = "Invalid transaction context. Missing cart details.";
  } else if (paymentOption === "in-store") {
    status = "success";
    message = "Your booking has been confirmed! Please pay at the store when you drop off or pick up your racket.";
  } else if (freeBooking === "1") {
    status = "success";
    message = "Your free booking has been confirmed! You will receive a confirmation email shortly.";
  } else if (status === "success") {
    message = "Your transaction has been verified. Your booking is now confirmed!";
  } else if (status === "processing") {
    message = PAYMENT_MESSAGES.processing;
  } else if (status === "failed") {
    message = PAYMENT_MESSAGES.failed;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        {status === "verifying" && (
          <div className={styles.loaderContainer}>
            <div className={styles.spinner}></div>
            <h2>Verifying Payment</h2>
            <p className={styles.message}>{message}</p>
          </div>
        )}

        {status === "processing" && (
          <div className={styles.loaderContainer}>
            <div className={styles.spinner}></div>
            <h2>Confirming Your Payment</h2>
            <p className={styles.message}>{message}</p>
            <button className={styles.closeBtn} onClick={checkAgain}>
              Check Again
            </button>
          </div>
        )}

        {status === "success" && (
          <div className={styles.successContainer}>
            <div className={styles.iconCircle}>
              <svg className={styles.svgIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h2>Booking Confirmed!</h2>
            <p className={styles.message}>{message}</p>
            {!skipVerification && (
              <div className={styles.infoBox}>
                <p>We have received your payment. Your racket service booking has been created.</p>
                <strong>You will receive your tax invoice directly via email shortly.</strong>
              </div>
            )}
            <p className={styles.actionNote}>You can now close this tab and return to your WhatsApp chat.</p>
            <button className={styles.closeBtn} onClick={() => window.close()}>
              Done & Return
            </button>
          </div>
        )}

        {status === "failed" && (
          <div className={styles.failedContainer}>
            <div className={`${styles.iconCircle} ${styles.errorCircle}`}>
              <svg className={styles.svgIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                <line x1="12" y1="9" x2="12" y2="13"></line>
                <line x1="12" y1="17" x2="12.01" y2="17"></line>
              </svg>
            </div>
            <h2>Payment Not Completed</h2>
            <p className={styles.message}>{message}</p>
            <p className={styles.actionNote}>If you believe you were charged, contact us via WhatsApp support and we'll check it for you.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsappSuccess;
